import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useGetTradableMarketAccountNamesForMultileg } from '@src/hooks/useGetTradableMarketAccountNamesForMultileg';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { useTradingSettings } from '@src/providers/TradingSettingsContext';
import { BlotterTable, Box, LoaderTalos, LoaderWrapper, logger, useCallbackRef, type Security } from '@talos/kyoko';
import type { CellValueChangedEvent, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme, type DefaultTheme } from 'styled-components';
import {
  multilegComboSliceActions,
  selectAutoOpenCell,
  selectCustomLegs,
  selectCustomLegsAsRows,
  selectIsEditing,
  selectIsEntryMode,
  selectIsSecuritiesReferenceDataReady,
} from './MultilegComboSlice';
import { AgMarketAccountRenderer } from './components/AgMarketAccountRenderer';
import { AgMultilegMarketAccountSelector } from './components/AgMultilegMarketAccountSelector';
import { AgSymbolSelector } from './components/AgSymbolSelector';
import { DEFAULT_COLUMN_DEFINITION, getDelta1ColDef } from './delta1ColDef';
import type { CustomLegRow, OptionStrategyRow } from './types';

export const Delta1SpreadScreen = ({ panelID }: { panelID: string }) => {
  const theme = useTheme();
  const dispatch = useAppStateDispatch();
  const { enableHaircutRatio, enableCFDs } = useFeatureFlag();
  const getTradableMarketAccountNamesForSecurity = useGetTradableMarketAccountNamesForMultileg();
  const { newMultilegDefaultDelta1Behaviour } = useTradingSettings();
  const customLegs = useAppStateSelector((state: AppState) => selectCustomLegs(state, panelID));

  const rowData: CustomLegRow[] = useAppStateSelector(state => selectCustomLegsAsRows(state, panelID));
  const isReferenceDataReady = useAppStateSelector(selectIsSecuritiesReferenceDataReady);

  const isEntryMode = useAppStateSelector(state => selectIsEntryMode(state, panelID));
  const isEditing = useAppStateSelector(state => selectIsEditing(state, panelID));
  const autoOpenCell = useAppStateSelector(state => selectAutoOpenCell(state, panelID));

  const context = useRef<{ theme: DefaultTheme }>({ theme });
  const gridApi = useRef<GridApi<CustomLegRow>>();
  const newMultilegDefaultingContext = useMemo(
    () => ({
      behaviour: newMultilegDefaultDelta1Behaviour,
      getTradableMarketAccountNames: getTradableMarketAccountNamesForSecurity,
    }),
    [getTradableMarketAccountNamesForSecurity, newMultilegDefaultDelta1Behaviour]
  );

  const handleCellValueChanged = useCallbackRef((ev: CellValueChangedEvent) => {
    const field = ev.colDef.field;

    if (!field || ev.rowIndex == null || !customLegs) {
      return;
    }

    switch (field) {
      case 'productType':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateProductTypeForLeg(ev.newValue, ev.rowIndex, enableCFDs),
            applyNewMultilegDefaulting: ev.rowIndex === 0,
            newMultilegDefaultingContext,
          })
        );
        break;
      case 'symbol':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateSymbol(ev.newValue, ev.rowIndex, getTradableMarketAccountNamesForSecurity),
            applyNewMultilegDefaulting: ev.rowIndex === 0,
            newMultilegDefaultingContext,
          })
        );
        break;
      case 'exchange':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateExchange(ev.newValue, ev.rowIndex, getTradableMarketAccountNamesForSecurity),
          })
        );
        break;
      case 'markets':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateMarketAccounts(ev.newValue, ev.rowIndex),
            isEditableParam: true,
          })
        );
        break;
      case 'expiry':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateExpiry(ev.newValue, ev.rowIndex, getTradableMarketAccountNamesForSecurity),
          })
        );
        break;
      case 'strike':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateStrike(ev.newValue, ev.rowIndex),
          })
        );
        break;
      case 'type':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateType(ev.newValue, ev.rowIndex),
          })
        );
        break;
      case 'initiating':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateInitiatingLegs(ev.newValue, ev.rowIndex),
            isEditableParam: true,
          })
        );
        break;
      case 'haircutRatio':
        dispatch(
          multilegComboSliceActions.updateCustomLegs({
            panelID,
            customLegs: customLegs.updateHaircutRatio(ev.newValue, ev.rowIndex),
            isEditableParam: true,
          })
        );
        break;

      default:
        logger.error(new Error(`Invalid modification on MultilegCombo, field: ${field}`));
    }
  });

  const handleSecurityChanged = useCallbackRef((security: Security, rowIndex: number) => {
    if (customLegs && security) {
      dispatch(
        multilegComboSliceActions.updateCustomLegs({
          panelID,
          customLegs: customLegs.updateSecurityForLeg(
            security,
            rowIndex,
            getTradableMarketAccountNamesForSecurity,
            enableCFDs
          ),
          applyNewMultilegDefaulting: rowIndex === 0,
          newMultilegDefaultingContext,
        })
      );
    }
  });

  const openAutoOpenCell = useCallbackRef(() => {
    if (!autoOpenCell || !gridApi.current) {
      return;
    }

    const { rowIndex, colKey } = autoOpenCell;
    gridApi.current.ensureIndexVisible(rowIndex);
    gridApi.current.ensureColumnVisible(colKey);

    requestAnimationFrame(() => {
      gridApi.current?.setFocusedCell(rowIndex, colKey);
      gridApi.current?.startEditingCell({ rowIndex, colKey });
      dispatch(multilegComboSliceActions.clearAutoOpenCell({ panelID }));
    });
  });

  useEffect(() => {
    openAutoOpenCell();
  }, [autoOpenCell, openAutoOpenCell, rowData]);

  const columnDefs = useMemo(
    () => getDelta1ColDef(handleSecurityChanged, customLegs, isEntryMode, isEditing, enableHaircutRatio, enableCFDs),
    [customLegs, handleSecurityChanged, isEntryMode, isEditing, enableHaircutRatio, enableCFDs]
  );

  if (!isReferenceDataReady) {
    return (
      <LoaderWrapper>
        <LoaderTalos />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <Box minHeight={110} h={110} data-testid="delta1-wrapper" id="multileg-deepdive-blotter-wrapper">
        <BlotterTable
          gridOptions={{
            // @ts-expect-error  We don't allow context to have just the required data
            context,
            getRowId: (params: GetRowIdParams<OptionStrategyRow>) => params.data.id,
            defaultColDef: DEFAULT_COLUMN_DEFINITION,
            columnDefs,
            singleClickEdit: true,
            stopEditingWhenCellsLoseFocus: true,
            rowData,
            rowHeight: 34,
            onCellValueChanged: handleCellValueChanged,
            onGridReady: (event: GridReadyEvent) => {
              gridApi.current = event.api;
              event.api.sizeColumnsToFit();
              openAutoOpenCell();
            },
            suppressContextMenu: true,
          }}
          extraComponents={{
            symbolSelector: AgSymbolSelector,
            multilegMarketAccountSelector: AgMultilegMarketAccountSelector,
            marketAccountRenderer: AgMarketAccountRenderer,
          }}
        />
      </Box>
    </>
  );
};
